<i18n>
{
  "fr": {
    "admin": {
      "obs-point-list": {
        "title": "Points d'observation",
        "update-value-tooltip": "Modifier le point d'observation",
        "delete-value-tooltip": "Supprimer le point d'observation",
        "add-value-tooltip": "Créer un point d'observation",
        "add-modal": {
          "title": "Créer un point d'observation",
          "obs-point-general-info": "Informations générales",
          "data-sources-info": "Sources de données",
          "coord-info": "Veuillez utiliser le système de coordonnées suivant: ",
          "datasource-documentation": "Plus d'information sur les sources de données correspondantes aux options choisies: ",
          "github-documentation": "Documentation (github)",
          "validated-datasource": "Données validées",
          "remote-datasource": "Télétransmission",
          "reference-datasource": "Références pour calculs de niveaux piézométriques",
          "forecast-datasource": "Prévision",
          "create-in-progress": "Création du point d'observation en cours...",
          "create-done": "Création du point d'observation terminée. Identifiant du nouveau point:",
          "refer-id-in-needed-subsystems": "Veuillez référencer cet identifiant dans les différents sous-systèmes nécessaires (SODA, REGIS etc.).",
          "goto-observation-point": "Aller au point d'observation",
          "groups-info": "Groupes"
        },
        "confirm-delete-title": "Confirmer la suppression",
        "confirm-delete-content": "La supression du point d'observation va supprimer toutes les données associées (chroniques, fichiers etc.)."
      }
    },
    "common": {
      "close": "Fermer",
      "cancel": "Annuler",
      "delete": "Supprimer",
      "create": "Créer",
      "ok": "ok",
      "options": "Options"
    },
    "observation-point": {
      "field": {
        "id": "ID",
        "publicId": "ID cantonal",
        "name": "Nom",
        "coords": "Coordonnées",
        "coord-x": "Coordonnée X",
        "coord-y": "Coordonnée Y",
        "type": "Type"
      }
    }
  },
  "de": {

  }
}
</i18n>

<template>
  <div class="admin fullpage list-observation-point">
    <div class="main-content">
      <h1>
        {{ $t('admin.obs-point-list.title') }}
        <md-button class="md-icon-button md-dense md-raised" @click="showObservationPointModal()">
          <md-icon>add</md-icon>
          <md-tooltip>{{ $t('admin.obs-point-list.add-value-tooltip') }}</md-tooltip>
        </md-button>
      </h1>
      <div class="loading" v-if="loading">
        <md-progress-spinner class="loading-animation" :md-diameter="100" :md-stroke="5" md-mode="indeterminate"></md-progress-spinner>
      </div>

      <div class="list-observation-point-content" v-if="!loading">
        <md-table v-if="observationPoints.length > 0">
          <md-table-row>
            <md-table-head>{{ $t('observation-point.field.id') }}</md-table-head>
            <md-table-head>{{ $t('observation-point.field.publicId') }}</md-table-head>
            <md-table-head>{{ $t('observation-point.field.name') }}</md-table-head>
            <md-table-head>{{ $t('observation-point.field.coords') }}</md-table-head>
            <md-table-head>{{ $t('common.options') }}</md-table-head>
          </md-table-row>
          <md-table-row v-for="obsPoint in filtredObservationPoints" :key="obsPoint.id">
            <md-table-cell>{{ obsPoint.id }}</md-table-cell>
            <md-table-cell>{{ obsPoint.publicId }}</md-table-cell>
            <md-table-cell>{{ obsPoint.name }}</md-table-cell>
            <md-table-cell>{{ obsPoint.geom.coordinates[0] | formatCoordinate }}, {{ obsPoint.geom.coordinates[1] | formatCoordinate }}</md-table-cell>
            <md-table-cell>
              <md-button class="md-icon-button md-dense" @click="editObservationPoint(obsPoint)">
                <md-icon>edit</md-icon>
                <md-tooltip>{{ $t('admin.obs-point-list.update-value-tooltip') }}</md-tooltip>
              </md-button>
              <md-button class="md-icon-button md-dense" @click="showConfirmDeleteObsPoint(obsPoint)">
                <md-icon>delete_forever</md-icon>
                <md-tooltip>{{ $t('admin.obs-point-list.delete-value-tooltip') }}</md-tooltip>
              </md-button>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </div>
    </div>

    <md-dialog class="add-observation-point" :md-active="showAddObservationPointModal" v-on:md-clicked-outside="closeObservationPointModal()">
      <md-dialog-title>{{ $t('admin.obs-point-list.add-modal.title') }}</md-dialog-title>

      <md-dialog-content v-if="createObsPointStep === 'idle'">
        <h3>{{ $t('admin.obs-point-list.add-modal.obs-point-general-info') }}</h3>
        <div class="additional-info">
          {{ $t('admin.obs-point-list.add-modal.coord-info') }} <a :href="`https://epsg.io/2056`" target="_blank">CH 1903+</a>
        </div>
        <div class="observation-point-info">
          <md-field>
            <label>{{ $t('observation-point.field.name') }}</label>
            <md-input v-model="observationPointToCreate.name"></md-input>
          </md-field>
          <md-field>
            <label>{{ $t('observation-point.field.publicId') }}</label>
            <md-input v-model="observationPointToCreate.publicId"></md-input>
          </md-field>
          <md-field>
            <label>{{ $t('observation-point.field.coord-x') }}</label>
            <md-input v-model="observationPointToCreate.geom.x"></md-input>
          </md-field>
          <md-field>
            <label>{{ $t('observation-point.field.coord-y') }}</label>
            <md-input v-model="observationPointToCreate.geom.y"></md-input>
          </md-field>
          <md-field>
            <label for="type">{{ $t('observation-point.field.type') }}</label>
            <md-select v-model="observationPointToCreate.type" md-dense name="type">
              <md-option v-for="type in observationPointTypes" :key="type" :value="type"> {{ type }} </md-option>
            </md-select>
          </md-field>
        </div>
        <h3>{{ $t('admin.obs-point-list.add-modal.data-sources-info') }}</h3>
        <div class="additional-info">
          {{ $t('admin.obs-point-list.add-modal.datasource-documentation') }} <a href="https://github.com/crealp/strates-db/wiki/Ajout-d'un-point-d'observation#sources-de-donn%C3%A9es-n%C3%A9cessaires" target="_blank">{{ $t('admin.obs-point-list.add-modal.github-documentation') }}</a>
        </div>
        <div class="data-sources-info">
          <md-switch v-model="datasourcesToCreate.validated">{{ $t('admin.obs-point-list.add-modal.validated-datasource') }}</md-switch>
          <md-switch v-model="datasourcesToCreate.remote">{{ $t('admin.obs-point-list.add-modal.remote-datasource') }}</md-switch>
          <md-switch v-model="datasourcesToCreate.reference">{{ $t('admin.obs-point-list.add-modal.reference-datasource') }}</md-switch>
          <md-switch v-model="datasourcesToCreate.forecast">{{ $t('admin.obs-point-list.add-modal.forecast-datasource') }}</md-switch>
        </div>
        <h3>{{ $t('admin.obs-point-list.add-modal.groups-info') }}</h3>
        <div class="groups-info">
          <div class="group parent" v-for="group in availableGroups" :key="group.id">
            <md-checkbox v-model="groupsToAssign" :value="group.id">#{{ group.id }} {{ group.name[$i18n.locale]}} ({{ group.code }})</md-checkbox>
            <div class="group child" v-for="subGroup in group.subGroups" :key="`${group.id}_${subGroup.id}`">
              <md-checkbox v-model="groupsToAssign" :value="subGroup.id">#{{ subGroup.id }} {{ subGroup.name[$i18n.locale]}} ({{ subGroup.code }})</md-checkbox>
            </div>
          </div>
        </div>
      </md-dialog-content>

      <md-dialog-content class="creating" v-if="createObsPointStep === 'create'">
        <md-progress-spinner class="loading-animation" :md-diameter="20" :md-stroke="4" md-mode="indeterminate"></md-progress-spinner>
        <p>{{ $t('admin.obs-point-list.add-modal.create-in-progress') }}</p>
      </md-dialog-content>

      <md-dialog-content class="done" v-if="createObsPointStep === 'done'">
        <p>{{ $t('admin.obs-point-list.add-modal.create-done') }}</p>
        <p class="obs-point-id">{{ createdObservationPoint.id }}</p>
        <p>{{ $t('admin.obs-point-list.add-modal.refer-id-in-needed-subsystems') }}</p>
      </md-dialog-content>

      <md-dialog-content class="error" v-if="createObsPointStep === 'error'">
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-primary md-raised" v-if="createObsPointStep === 'idle'" @click="createObservationPoint()">{{ $t('common.create') }}</md-button>
        <md-button class="md-primary" v-if="createObsPointStep === 'done'" @click="editObservationPoint(createdObservationPoint)">{{ $t('admin.obs-point-list.add-modal.goto-observation-point') }}</md-button>
        <md-button class="md-primary" v-if="createObsPointStep !== 'creating'" @click="closeObservationPointModal()">{{ $t('common.close') }}</md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog-confirm
      :md-active.sync="confirmDeleteObsPoint"
      :md-title="$t('admin.obs-point-list.confirm-delete-title')"
      :md-content="$t('admin.obs-point-list.confirm-delete-content')"
      :md-confirm-text="$t('common.delete')"
      :md-cancel-text="$t('common.cancel')"
      @md-cancel="onDeleteObsPointCanceled"
      @md-confirm="deleteObservationPoint"
    />
  </div>
</template>

<script>
import ObservationPointProxy from '@/proxies/observationPoint'
import GroupProxy from '@/proxies/group'

export default {
  name: 'ListObservationPointAdmin',
  data () {
    return {
      loading: true,
      showAddObservationPointModal: false,
      createObsPointStep: 'idle',
      observationPoints: [],
      observationPointTypes: ["S", "W"],
      observationPointToCreate: ObservationPointProxy.emptyObservationPoint,
      createdObservationPoint: {},
      datasourcesToCreate: {
        validated: false,
        remote: false,
        reference: false,
        forecast: false
      },
      availableGroups: [],
      groupsToAssign: [],
      confirmDeleteObsPoint: false,
      obsPointIdToDelete: null
    }
  },
  computed: {
    filtredObservationPoints () {
      return this.observationPoints
    }
  },
  mounted () {
    this.load()
  },
  filters: {
    formatCoordinate (value) {
      return new Intl.NumberFormat('fr-CH', {
        style: 'decimal'
      }).format(value.toFixed(0)).replaceAll(' ', '\'')
    }
  },
  methods: {
    load () {
      this.loading = true
      let toFetch = [
        ObservationPointProxy.list(),
        GroupProxy.list(),
        ObservationPointProxy.listTypes()
      ]
      Promise.all(toFetch).then((data) => {
        this.observationPoints = data[0]
        this.availableGroups = data[1]
        this.observationPointTypes = data[2]
        this.loading = false
      })
    },
    editObservationPoint (observationPoint) {
      this.$router.push({ name: 'admin-observation-point-data', params: { id: observationPoint.id } })
    },
    showObservationPointModal() {
      this.observationPointToCreate = ObservationPointProxy.emptyObservationPoint
      this.groupsToAssign = []
      this.datasourcesToCreate = {
        validated: false,
        remote: false,
        reference: false,
        forecast: false
      }
      this.createObsPointStep = 'idle'
      this.showAddObservationPointModal = true
    },
    closeObservationPointModal () {
      this.showAddObservationPointModal = false
    },
    createObservationPoint () {
      this.createObsPointStep = 'create'
      this.observationPointToCreate.geom.x = parseFloat(this.observationPointToCreate.geom.x)
      this.observationPointToCreate.geom.y = parseFloat(this.observationPointToCreate.geom.y)
      ObservationPointProxy.createWithDatasources(this.observationPointToCreate, this.datasourcesToCreate).then((obsPoint) => {
        this.createdObservationPoint = obsPoint
        
        // Assignation du point d'observation aux différentes groupes
        let toFetch = this.groupsToAssign.map((groupId) => GroupProxy.toggleObservationPoint(groupId, this.createdObservationPoint.id))

        Promise.all(toFetch).then(() => {
          this.createObsPointStep = 'done'
        }).catch(this.onCreateObservationPointError)
      }).catch(this.onCreateObservationPointError)
    },
    showConfirmDeleteObsPoint (obsPoint) {
      this.obsPointIdToDelete = obsPoint.id
      this.confirmDeleteObsPoint = true
    },
    deleteObservationPoint () {
      ObservationPointProxy.delete(this.obsPointIdToDelete).finally(() => {
        this.load()
      })
    },
    onDeleteObsPointCanceled () {
      this.obsPointIdToDelete = null
      this.confirmDeleteObsPoint = false
    },
    onCreateObservationPointError () {
      this.createObsPointStep = 'error'
    }
  }
}
</script>

<style lang="scss">
  .add-observation-point .md-dialog-container {
    max-width: 760px;
    max-height: 90%;
  }
</style>

<style lang="scss" scoped>
  .list-observation-point {
    h1 {
      display: flex;
      align-items: center;
      gap: 10px;
    }
  }

  .modal-content {
    padding-left: 25px;
    padding-right: 25px;

    &.creating {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 15px;
      .md-progress-spinner {
        width: 20px;
        height: 20px;
      }
    }

    &.done {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      max-width: 390px;
      text-align: center;

      .obs-point-id {
        font-weight: 600;
        font-size: 30px;
        margin: 10px;
      }
    }

    .additional-info {
      text-align: center;
      margin-top: 10px;
      font-style: italic;
    }

    .observation-point-info {
      display: flex;
      flex-wrap: wrap;
      gap: 25px;
      row-gap: 0px;
      justify-content: center;

      .md-field {
        max-width: 290px;
      }
    }

    .data-sources-info {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      row-gap: 0px;
    }

    .groups-info {
      max-height: 275px;
      overflow: auto;
      margin-left: 15px;
      margin-right: 15px;

      .md-checkbox {
        margin: 0px;
      }

      .group {
        .child {
          margin-left: 38px;
        }
      }
    }
  }
</style>
